import { Plan } from '../types';

interface Props {
  form: Omit<Plan, 'id'>;
  set: (f: Partial<Omit<Plan, 'id'>>) => void;
}

const Field = ({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) => (
  <div className="mb-4">
    <label className="block text-[12px] text-white/50 mb-1.5">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-white/30 mt-1">{hint}</p>}
  </div>
);

const inputCls = 'w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[14px] outline-none focus:border-violet-500 transition-colors';

const Toggle = ({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) => (
  <button type="button" onClick={() => onChange(!value)} className={`w-11 h-6 rounded-full transition-colors cursor-pointer relative shrink-0 overflow-hidden ${value ? 'bg-violet-600' : 'bg-white/10'}`}>
    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${value ? 'left-6' : 'left-1'}`} />
  </button>
);

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-center justify-between py-3 border-b border-white/5">
    <span className="text-[13px] text-white/60">{label}</span>
    <div>{children}</div>
  </div>
);

const PlanEditorGeneral = ({ form, set }: Props) => (
  <div>
    <div className="grid grid-cols-2 gap-4">
      <Field label="Nome do plano *">
        <input value={form.name} onChange={(e) => set({ name: e.target.value })} className={inputCls} placeholder="Ex: Pro Anual" />
      </Field>
      <Field label="Slug *" hint="Usado como identificador único">
        <input
          value={form.slug}
          onChange={(e) => set({ slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
          className={`${inputCls} font-mono`}
          placeholder="pro-anual"
        />
      </Field>
    </div>

    <Field label="Descrição curta" hint="Exibida nos cards de plano — max 200 chars">
      <textarea
        value={form.description}
        onChange={(e) => set({ description: e.target.value })}
        rows={2}
        maxLength={200}
        className={`${inputCls} resize-none`}
        placeholder="Ideal para usuários que querem o máximo do sistema..."
      />
    </Field>

    <Field label="Descrição comercial" hint="Texto mais completo para landing pages e comparativos">
      <textarea
        value={form.commercial_description}
        onChange={(e) => set({ commercial_description: e.target.value })}
        rows={3}
        maxLength={500}
        className={`${inputCls} resize-none`}
        placeholder="Desbloqueie todos os recursos premium e gerencie suas finanças sem limites..."
      />
    </Field>

    <Field label="Texto promocional" hint="Ex: 'Mais popular', 'Oferta limitada', texto badge opcional">
      <input
        value={form.promotional_text}
        onChange={(e) => set({ promotional_text: e.target.value })}
        className={inputCls}
        placeholder="Mais popular"
      />
    </Field>

    <div className="grid grid-cols-2 gap-4 mb-4">
      <Field label="Ordem de exibição" hint="Menor número aparece primeiro">
        <input type="number" min={0} value={form.display_order} onChange={(e) => set({ display_order: Number(e.target.value) })} className={inputCls} />
      </Field>
      <Field label="Moeda">
        <select value={form.currency} onChange={(e) => set({ currency: e.target.value })} className={`${inputCls} cursor-pointer`}>
          <option value="BRL" className="bg-[#1A1A2E]">BRL — Real</option>
          <option value="USD" className="bg-[#1A1A2E]">USD — Dólar</option>
          <option value="EUR" className="bg-[#1A1A2E]">EUR — Euro</option>
        </select>
      </Field>
    </div>

    <div className="bg-white/3 border border-white/8 rounded-2xl p-4 space-y-0">
      <p className="text-[11px] text-white/30 uppercase tracking-wider font-600 mb-2">Visibilidade e rótulos comerciais</p>
      <Row label="Ativo"><Toggle value={form.is_active} onChange={(v) => set({ is_active: v })} /></Row>
      <Row label="Público (visível na página de planos)"><Toggle value={form.is_public} onChange={(v) => set({ is_public: v })} /></Row>
      <Row label="Em destaque"><Toggle value={form.is_featured} onChange={(v) => set({ is_featured: v })} /></Row>
      <Row label="Recomendado"><Toggle value={form.is_recommended} onChange={(v) => set({ is_recommended: v })} /></Row>
      <Row label="Melhor custo-benefício"><Toggle value={form.is_best_value} onChange={(v) => set({ is_best_value: v })} /></Row>
      <Row label="Plano legado (mantido apenas para usuários antigos)"><Toggle value={form.is_legacy} onChange={(v) => set({ is_legacy: v })} /></Row>
      <Row label="Plano interno/oculto (não aparece para o público)"><Toggle value={form.is_internal} onChange={(v) => set({ is_internal: v })} /></Row>
    </div>
  </div>
);

export default PlanEditorGeneral;
